import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { useEffect, useState } from 'react';
import { addNumericSuffix } from '../../util/util';
import { Info } from 'luxon';
import { DurationUnit, RecurringType, RentalProductPrice } from '../../clients/quipli-service/pricing/pricing';
import { filter, keyBy, partition, sortBy } from 'lodash';
import './product-pricing-table.scss';
import { OrderType } from '../../clients/quipli-service/order/order';

export const ProductPricingTable = ({
  productId,
  locationId,
  showRangePriceUnitsConf,
  tagDiscount,
}: {
  productId: number;
  locationId?: number;
  showRangePriceUnitsConf?: boolean;
  tagDiscount: number;
}) => {
  const { listPricesForMultipleProducts, getConfiguration } = useCustomerAPI();
  const [thePrices, setThePrices] = useState<RentalProductPrice[]>([]);
  const [featuredPrices, setFeaturedPrices] = useState<
    { price: number; priceLabel: string; durationLabel: string; activeOn: string; featured: boolean }[]
  >([]);
  const [showRangePriceUnits, setShowRangePriceUnits] = useState<boolean | undefined>(showRangePriceUnitsConf);

  const currencyFormat = Intl.NumberFormat('us-en', { currency: 'USD', minimumFractionDigits: 2 });
  const translateDaysOfWeek = (dow: number[]): string =>
    dow.length === 7 ? 'week' : dow.map((day) => Info.weekdays('short')[day - 1]).join(', ');
  const translateDaysOfMonth = (daysOfMonth: number[]): string => {
    return daysOfMonth.map((day) => (day === -1 ? 'last' : addNumericSuffix(day))).join(', ');
  };
  const translateWeeksOfMonth = (wom: number[]): string =>
    wom.map((week) => (week === -1 ? 'last' : addNumericSuffix(week))).join(', ');

  useEffect(() => {
    if (showRangePriceUnitsConf === undefined) {
      getConfiguration('CONF_SHOW_RANGE_PRICE_UNITS').then((showRangePriceUnits) =>
        setShowRangePriceUnits(showRangePriceUnits?.value === '1'),
      );
    } else setShowRangePriceUnits(showRangePriceUnitsConf);
  }, [showRangePriceUnitsConf]);

  useEffect(() => {
    const allPrices: RentalProductPrice[] = [];
    const [locationPrices, globalPrices] = partition(
      thePrices.filter((p) => p.durationUnit !== DurationUnit.minute),
      (p) => (p.locationIds ?? []).length > 0,
    );
    // have location prices override global prices
    const rentalProductPrices = keyBy(
      locationPrices,
      (p) =>
        `${p.durationValue}/${p.durationUnit}/${p.recurringType}/${p.recurringDaysOfWeek}/${p.recurringDaysOfMonth}/${p.recurringWeeksOfMonth}`,
    );
    globalPrices.forEach((p) => {
      const key = `${p.durationValue}/${p.durationUnit}/${p.recurringType}/${p.recurringDaysOfWeek}/${p.recurringDaysOfMonth}/${p.recurringWeeksOfMonth}`;
      const existing = rentalProductPrices[key];
      if (!existing) rentalProductPrices[key] = p;
      else if (existing && existing.from && existing.to) allPrices.push(p); // existing location price has a date range, so still show global price too
    });

    for (const k in rentalProductPrices) {
      allPrices.push(rentalProductPrices[k]);
    }
    const formattedPrices = filter(allPrices, (rpp) => rpp.featured).map((rpp) => {
      const fromFormatted = rpp.from?.toFormat('LLLL d, y');
      const toFormatted = rpp.to?.toFormat('LLLL d, y');
      const activeOn =
        rpp.recurringType === RecurringType.Weekly
          ? `Every ${translateDaysOfWeek(rpp.recurringDaysOfWeek!)}`
          : rpp.recurringType === RecurringType.Biweekly
          ? `Every other ${translateDaysOfWeek(rpp.recurringDaysOfWeek!)}`
          : rpp.recurringType === RecurringType.DateMonthly
          ? `Every ${translateDaysOfMonth(rpp.recurringDaysOfMonth!)} of the month`
          : rpp.recurringType === RecurringType.DayOfWeekMonthly
          ? `Every ${translateWeeksOfMonth(rpp.recurringWeeksOfMonth!)} ${translateDaysOfWeek(
              rpp.recurringDaysOfWeek!,
            )} of the month`
          : rpp.from && rpp.to
          ? `${fromFormatted}${fromFormatted === toFormatted ? '' : ` - ${rpp.to.toFormat('LLLL d, y')}`}`
          : 'Every day';
      const duration =
        rpp.durationLabel ??
        `${rpp.durationValue === 1 ? '' : `${rpp.durationValue} `}${rpp.durationUnit}${
          rpp.durationValue > 1 ? 's' : ''
        }`;
      return {
        priceLabel: `$${currencyFormat.format((rpp.price * tagDiscount) / 100)}${
          rpp.featured ? '' : ` per ${duration}`
        }`,
        durationLabel: showRangePriceUnits ? duration : `Per ${duration}`,
        activeOn,
        featured: rpp.featured,
        price: rpp.price * tagDiscount,
        durationMinutes: rpp.durationMinutes,
      };
    });
    setFeaturedPrices(sortBy(formattedPrices, (f) => f.durationMinutes));
  }, [thePrices]);

  useEffect(() => {
    listPricesForMultipleProducts([productId], locationId, OrderType.Rental).then((p) =>
      setThePrices((p.get(productId) || []) as RentalProductPrice[]),
    );
  }, [productId, locationId]);

  return (
    <div className="product-pricing-table font-sans">
      <div className="auto-cols-auto border font-bold border-solid border-gray-200 rounded flex align-center py-4 mb-6 flex-wrap justify-center items-center">
        {[...featuredPrices].map((fp, i) => {
          return (
            <div
              key={i}
              className="items-center flex grow max-w-[50%] flex-col border border-solid  border-gray-200 border-l-0 border-y-0 border-r last-of-type:border-r-0"
            >
              <span className="text-[10px] text-gray-600 mb-1">{fp.durationLabel}</span>
              <span>{fp.priceLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
